/**
 * `DIALOG.projectSettings` (W2-06; DESIGN.md §4.21, §5.6.7, §6.10, §6.11; rpe-format F§9.1, F§10.2, F§10.5): one
 * surface for every chart-level setting, every change one undoable transaction:
 *  - Display: aspect ratio (presets or a number) and "force", background dim;
 *  - Gameplay rendering: holds stay visible until the tail passes, width also scales note height, attach-UI fix,
 *    line length;
 *  - Speed mode: Classic / RPE 1.7 speed curves through a switch step that explains the change, asks about the
 *    chart's RPEVersion below 170 (raise to 170 or keep, never automatic) and about preserved RPE speed easings;
 *    RPEVersion and the bake tolerance (Full);
 *  - Song-select preview: start / end with play, validated against the song length (Phira refuses to open the chart
 *    when the preview ends after the song) and a one-click clamp;
 *  - Export defaults: profile and file names.
 * `section` scrolls to and focuses that section when the dialog opens. `inspector.focusField` on a chart-level field
 * (`pkg.*`, `meta.preview*`: Problems "Go", the PH-36 "Edit preview range" fix) focuses that field's control: a
 * pending field when the command opens the dialog, a focus request while it is open. A Full-only field asked for
 * at the Guided level (RPEVersion, bake tolerance) is revealed.
 */

import { useCallback, useEffect, useId, useRef, useState, type ReactNode } from 'react';
import type { AppServices, DialogProps } from '../../app/registry/types';
import type { ProjectSettingsProps } from '../../app/dialogs/types';
import { Core } from '../../core/commands/index';
import type { ChartDoc, ChartMeta, ExportProfile, PackageSettings, SpeedMode } from '../../core/model/types';
import { useT } from '../../i18n/useT';
import type { MessageKey } from '../../i18n/types';
import type { DocStore } from '../../state/types';
import type { BadgeProps } from '../../ui/types';
import { Badge, Button, Checkbox, Dialog, Field, NumberField, Segmented, Select, Slider } from '../../ui';
import { clampPreview } from './commands';
import {
  clearPendingSettingsField,
  focusFieldIn,
  FULL_ONLY_SETTINGS,
  isSettingsField,
  onFocusFieldRequest,
  peekPendingSettingsField,
  settingsSectionOf,
} from './focusField';
import { useDocValue, useLevel, useServicesStore, useTopicStamp } from './hooks';
import { m } from './inspector.messages';
import { ignoredEaseCount, musicDuration, previewRangeIssue } from './model';
import { Callout, cx, fmtNum, Readout, Row, WithInfo } from './parts';
import s from './Inspector.module.css';
import { useEditKit, type EditKit } from './tx';
import { FIELD, useFieldProblems } from './useFieldProblems';

export type ProjectSettingsSection = NonNullable<ProjectSettingsProps['section']> | 'preview';

/** Aspect ratio presets (width / height). */
export const ASPECT_PRESETS: ReadonlyArray<{ readonly id: string; readonly ratio: number }> = [
  { id: '16:9', ratio: 16 / 9 },
  { id: '4:3', ratio: 4 / 3 },
  { id: '3:2', ratio: 3 / 2 },
  { id: '16:10', ratio: 16 / 10 },
  { id: '2:1', ratio: 2 },
  { id: '21:9', ratio: 21 / 9 },
];

export function aspectPresetOf(ratio: number): string {
  return ASPECT_PRESETS.find((p) => Math.abs(p.ratio - ratio) < 1e-6)?.id ?? 'custom';
}

export function ProjectSettingsDialog(props: DialogProps<'projectSettings'>) {
  const store = useServicesStore(props.services);
  const t = useT();
  if (store === null) {
    return (
      <Dialog
        open
        size="sm"
        title={t(m.psTitle)}
        onClose={() => {
          props.close(null);
        }}
        footer={
          <Button
            variant="primary"
            onClick={() => {
              props.close(null);
            }}
          >
            {t(m.done)}
          </Button>
        }
      >
        <p>{t(m.psNoProject)}</p>
      </Dialog>
    );
  }
  return (
    <ProjectSettingsBody
      services={props.services}
      store={store}
      section={props.props.section}
      close={() => {
        props.close(null);
      }}
    />
  );
}

/** A field to focus (and its section), or only a section; `seq` makes a repeated request focus again. */
interface FocusRequest {
  readonly field: string | null;
  readonly section: ProjectSettingsSection | null;
  readonly seq: number;
}

function pkgOf(doc: ChartDoc): PackageSettings {
  return doc.pkg;
}
function metaOf(doc: ChartDoc): ChartMeta {
  return doc.meta;
}

function ProjectSettingsBody(props: {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly section: ProjectSettingsProps['section'];
  readonly close: () => void;
}) {
  const { services, store } = props;
  const t = useT();
  const pkg = useDocValue(store, pkgOf, ['pkg']);
  const edit = useEditKit(services, store, 'project');
  const badges = useFieldProblems(services, {});
  const sections = useRef(new Map<string, HTMLElement>());
  const body = useRef<HTMLDivElement>(null);
  const level = useLevel(services);
  // What to focus: a field (inspector.focusField), else the requested section's heading.
  const [request, setRequest] = useState<FocusRequest | null>(() => {
    const field = peekPendingSettingsField();
    if (field !== null) return { field, section: settingsSectionOf(field), seq: 0 };
    return props.section === undefined ? null : { field: null, section: props.section, seq: 0 };
  });
  // A Full-only field asked for at the Guided level stays shown while the dialog is open.
  const [revealFull, setRevealFull] = useState(() => request?.field != null && FULL_ONLY_SETTINGS.has(request.field));
  useEffect(() => {
    clearPendingSettingsField();
  }, []);
  // Requests while the dialog is open.
  useEffect(
    () =>
      onFocusFieldRequest((field) => {
        if (!isSettingsField(field)) return false;
        setRequest((r) => ({ field, section: settingsSectionOf(field), seq: (r?.seq ?? 0) + 1 }));
        if (FULL_ONLY_SETTINGS.has(field)) setRevealFull(true);
        return true;
      }),
    [],
  );
  useEffect(() => {
    if (request === null) return;
    // After the dialog's portal mounted and its own initial focus (Radix focuses the first control on open).
    const timer = setTimeout(() => {
      const root = body.current;
      if (root !== null && request.field !== null && focusFieldIn(root, request.field)) return;
      const el = request.section === null ? undefined : sections.current.get(request.section);
      if (el === undefined) return;
      if (typeof el.scrollIntoView === 'function') el.scrollIntoView({ block: 'start' });
      el.querySelector<HTMLElement>('h3')?.focus();
    }, 0);
    return () => {
      clearTimeout(timer);
    };
  }, [request]);
  const showFull = level === 'full' || revealFull;
  const register = useCallback(
    (id: string) => (el: HTMLElement | null) => {
      if (el === null) sections.current.delete(id);
      else sections.current.set(id, el);
    },
    [],
  );
  const patchPkg = (field: string, p: Parameters<typeof Core.doc.patchPkg>[1]['patch']): void => {
    edit.commit(field, { key: m.txPkg }, (tx) => {
      Core.doc.patchPkg(tx, { patch: p });
    });
  };

  return (
    <Dialog
      open
      size="lg"
      title={t(m.psTitle)}
      description={t(m.psDescription)}
      onClose={props.close}
      footer={
        <Button variant="primary" onClick={props.close}>
          {t(m.done)}
        </Button>
      }
    >
      <div className={s.dialogBody} ref={body}>
        {/* ---------------- Display */}
        <PsSection id="display" title={t(m.psDisplay)} register={register}>
          <Row edit={edit} field={FIELD.pkgAspect}>
            <div className={s.pair}>
              <Field label={t(m.aspectPreset)} helpId="pkg.aspectPreset">
                <Select<string>
                  ariaLabel={t(m.aspectPreset)}
                  value={aspectPresetOf(pkg.aspectRatio)}
                  options={[
                    ...ASPECT_PRESETS.map((p) => ({ value: p.id, label: p.id })),
                    { value: 'custom', label: t(m.aspectCustom) },
                  ]}
                  onChange={(v) => {
                    const preset = ASPECT_PRESETS.find((p) => p.id === v);
                    if (preset !== undefined) patchPkg(FIELD.pkgAspect, { aspectRatio: preset.ratio });
                  }}
                />
              </Field>
              <NumberField
                label={t(m.aspectRatio)}
                value={{ kind: 'value', value: pkg.aspectRatio }}
                step={0.01}
                min={0.1}
                precision={4}
                help={{ key: m.aspectHelp }}
                helpId={FIELD.pkgAspect}
                badges={badges(FIELD.pkgAspect)}
                onCommit={(v) => {
                  if (v.kind === 'number' && v.value > 0) patchPkg(FIELD.pkgAspect, { aspectRatio: v.value });
                }}
              />
            </div>
          </Row>
          <Row edit={edit} field="pkg.forceAspectRatio">
            <Checkbox
              label={t(m.forceAspect)}
              checked={pkg.forceAspectRatio}
              helpId="pkg.forceAspectRatio"
              onChange={(on) => {
                patchPkg('pkg.forceAspectRatio', { forceAspectRatio: on });
              }}
            />
            <Readout>{t(m.forceAspectHelp)}</Readout>
          </Row>
          <Row edit={edit} field="pkg.backgroundDim">
            <div data-focus-field="pkg.backgroundDim">
              <Slider
                label={t(m.backgroundDim)}
                value={pkg.backgroundDim}
                min={0}
                max={1}
                step={0.01}
                format={(v) => t(m.percentValue, { value: v })}
                onChange={(v, final) => {
                  edit.scrub(
                    'pkg.backgroundDim',
                    { key: m.txPkg },
                    (tx) => {
                      Core.doc.patchPkg(tx, { patch: { backgroundDim: v } });
                    },
                    final,
                  );
                }}
              />
            </div>
            <Readout>{t(m.backgroundDimHelp)}</Readout>
          </Row>
        </PsSection>

        {/* ---------------- Gameplay rendering */}
        <PsSection id="gameplay" title={t(m.psGameplay)} register={register}>
          <FlagRow
            edit={edit}
            field="pkg.holdPartialCover"
            label={m.holdPartialCover}
            help={m.holdPartialCoverHelp}
            rpe="holdPartialCover"
            checked={pkg.holdPartialCover}
            onChange={(on) => {
              patchPkg('pkg.holdPartialCover', { holdPartialCover: on });
            }}
          />
          <FlagRow
            edit={edit}
            field="pkg.noteUniformScale"
            label={m.noteUniformScale}
            help={m.noteUniformScaleHelp}
            rpe="noteUniformScale"
            checked={pkg.noteUniformScale}
            onChange={(on) => {
              patchPkg('pkg.noteUniformScale', { noteUniformScale: on });
            }}
          />
          <FlagRow
            edit={edit}
            field={FIELD.pkgAttachUiFix}
            label={m.attachUiFix}
            help={m.attachUiFixHelp}
            rpe="useAttachUiFix"
            checked={pkg.useAttachUiFix}
            badges={badges(FIELD.pkgAttachUiFix)}
            onChange={(on) => {
              patchPkg(FIELD.pkgAttachUiFix, { useAttachUiFix: on });
            }}
          />
          <Row edit={edit} field="pkg.lineLength">
            <NumberField
              label={t(m.lineLength)}
              value={{ kind: 'value', value: pkg.lineLength }}
              step={0.5}
              min={0.01}
              precision={3}
              help={{ key: m.lineLengthHelp }}
              helpId="pkg.lineLength"
              onCommit={(v) => {
                if (v.kind === 'number' && v.value > 0) patchPkg('pkg.lineLength', { lineLength: v.value });
              }}
            />
          </Row>
        </PsSection>

        {/* ---------------- Speed mode */}
        <PsSection id="speed" title={t(m.psSpeed)} register={register}>
          <SpeedModePanel store={store} edit={edit} pkg={pkg} badges={badges} showFull={showFull} />
        </PsSection>

        {/* ---------------- Song-select preview */}
        <PsSection id="preview" title={t(m.psPreview)} register={register}>
          <PreviewPanel services={services} store={store} edit={edit} badges={badges} />
        </PsSection>

        {/* ---------------- Export defaults */}
        <PsSection id="export" title={t(m.psExport)} register={register}>
          <Row edit={edit} field="pkg.exportProfile">
            <ChoiceList<ExportProfile>
              legend={t(m.exportProfile)}
              focusField="pkg.exportProfile"
              value={pkg.exportProfile}
              options={[
                { value: 'rpe-compatible', label: t(m.profileRpe), description: t(m.profileRpeDesc) },
                { value: 'minimal', label: t(m.profileMinimal), description: t(m.profileMinimalDesc) },
              ]}
              onChange={(v) => {
                edit.once({ key: m.txPkg }, (tx) => {
                  Core.doc.patchPkg(tx, { patch: { exportProfile: v } });
                });
              }}
            />
          </Row>
          <Row edit={edit} field="pkg.fileNaming">
            <ChoiceList<PackageSettings['fileNaming']>
              legend={t(m.fileNaming)}
              focusField="pkg.fileNaming"
              value={pkg.fileNaming}
              options={[
                { value: 'readable', label: t(m.namingReadable) },
                { value: 'rpe-id', label: t(m.namingRpeId, { id: store.doc.meta.rpeId }) },
              ]}
              onChange={(v) => {
                edit.once({ key: m.txPkg }, (tx) => {
                  Core.doc.patchPkg(tx, { patch: { fileNaming: v } });
                });
              }}
            />
          </Row>
          {pkg.infoOffsetSec !== 0 && <Readout>{t(m.infoOffsetHelp, { sec: fmtNum(pkg.infoOffsetSec, 6) })}</Readout>}
        </PsSection>
      </div>
    </Dialog>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Pieces
// ---------------------------------------------------------------------------------------------------------------

function PsSection(props: {
  readonly id: ProjectSettingsSection;
  readonly title: string;
  readonly register: (id: string) => (el: HTMLElement | null) => void;
  readonly children: ReactNode;
}) {
  const headingId = useId();
  return (
    <section
      ref={props.register(props.id)}
      className={s.dialogSection}
      aria-labelledby={headingId}
      data-section={props.id}
    >
      <h3 id={headingId} className={s.subheading} tabIndex={-1}>
        {props.title}
      </h3>
      {props.children}
    </section>
  );
}

function FlagRow(props: {
  readonly edit: EditKit;
  readonly field: string;
  readonly label: MessageKey;
  readonly help: MessageKey;
  readonly rpe: string;
  readonly checked: boolean;
  readonly badges?: readonly BadgeProps[];
  readonly onChange: (on: boolean) => void;
}) {
  const t = useT();
  return (
    <Row edit={props.edit} field={props.field}>
      <WithInfo info={{ text: props.help, rpe: props.rpe }}>
        <Checkbox label={t(props.label)} checked={props.checked} helpId={props.field} onChange={props.onChange} />
      </WithInfo>
      <Readout>{t(props.help)}</Readout>
      {props.badges !== undefined && props.badges.length > 0 && (
        <div className={s.badgeLine}>
          {props.badges.map((b, i) => (
            <Badge key={i} {...b} />
          ))}
        </div>
      )}
    </Row>
  );
}

/** Radio list with descriptions (native inputs: arrow keys move, Space picks). */
export function ChoiceList<T extends string>(props: {
  readonly legend: string;
  /** Field path `inspector.focusField` lands on (the checked choice). */
  readonly focusField?: string;
  readonly value: T | null;
  readonly options: ReadonlyArray<{ readonly value: T; readonly label: string; readonly description?: string }>;
  onChange(v: T): void;
}) {
  const name = useId();
  return (
    <fieldset className={s.choiceList} data-focus-field={props.focusField}>
      <legend className={s.subheading}>{props.legend}</legend>
      {props.options.map((o) => (
        <label key={o.value} className={s.choice}>
          <input
            type="radio"
            name={name}
            value={o.value}
            checked={props.value === o.value}
            onChange={() => {
              props.onChange(o.value);
            }}
          />
          <span>{o.label}</span>
          {o.description !== undefined && <span className={s.choiceDesc}>{o.description}</span>}
        </label>
      ))}
    </fieldset>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Speed mode
// ---------------------------------------------------------------------------------------------------------------

function SpeedModePanel(props: {
  readonly store: DocStore;
  readonly edit: EditKit;
  readonly pkg: PackageSettings;
  readonly badges: (field: string) => readonly BadgeProps[];
  /** Full level, or a Full-only field was asked for. */
  readonly showFull: boolean;
}) {
  const { store, edit, pkg } = props;
  const t = useT();
  const ignored = useDocValue(store, ignoredEaseCount, ['keys', 'tracks']);
  const [target, setTarget] = useState<SpeedMode | null>(null);
  const [rpeVersion, setRpeVersion] = useState<'raise' | 'keep'>('raise');
  const [ignoredEase, setIgnoredEase] = useState<'drop' | 'apply'>('drop');
  const mode = pkg.speedMode;
  const sub =
    pkg.rpeVersion >= 170
      ? t(m.speedSubModern, { version: pkg.rpeVersion })
      : t(m.speedSubLegacy, { version: pkg.rpeVersion });

  const confirm = (): void => {
    if (target === null) return;
    const args =
      target === 'rpe170'
        ? {
            mode: target,
            ignoredEase: ignored > 0 ? ignoredEase : ('drop' as const),
            rpeVersion: pkg.rpeVersion < 170 ? rpeVersion : ('keep' as const),
          }
        : { mode: target, ignoredEase: 'drop' as const, rpeVersion: 'keep' as const };
    const out = edit.once({ key: m.txSpeedMode }, (tx) => {
      Core.doc.setSpeedMode(tx, args);
    });
    if (out.ok) setTarget(null);
  };

  return (
    <>
      <Row edit={edit} field={FIELD.pkgSpeedMode}>
        <Field
          label={t(m.psSpeed)}
          helpId={FIELD.pkgSpeedMode}
          badges={props.badges(FIELD.pkgSpeedMode)}
          info={{ text: { key: m.speedRpe170Desc }, rpeTerm: 'useRpe170Speed' }}
        >
          <strong data-testid="speed-mode">{t(mode === 'rpe170' ? m.speedRpe170 : m.speedClassic)}</strong>
        </Field>
        <Readout>{t(mode === 'rpe170' ? m.speedRpe170Desc : m.speedClassicDesc)}</Readout>
        {mode === 'rpe170' && <Readout testId="speed-sub-mode">{sub}</Readout>}
        {target === null && (
          <div className={s.actions} data-focus-field={FIELD.pkgSpeedMode}>
            <Button
              size="sm"
              icon="Gauge"
              onClick={() => {
                setRpeVersion('raise');
                setIgnoredEase('drop');
                setTarget(mode === 'rpe170' ? 'legacy' : 'rpe170');
              }}
            >
              {t(mode === 'rpe170' ? m.switchToClassic : m.switchToRpe170)}
            </Button>
          </div>
        )}
      </Row>

      {target !== null && (
        <div
          className={s.confirmBox}
          role="group"
          aria-label={t(m.switchTitle)}
          data-testid="speed-switch"
          data-focus-field={FIELD.pkgSpeedMode}
        >
          <strong>{t(m.switchTitle)}</strong>
          <p className={cx(s.readout)}>{t(target === 'rpe170' ? m.switchToRpe170Body : m.switchToClassicBody)}</p>
          {target === 'rpe170' && pkg.rpeVersion < 170 && (
            <>
              <p className={s.readout}>{t(m.rpeVersionQuestion, { version: pkg.rpeVersion })}</p>
              <ChoiceList<'raise' | 'keep'>
                legend={t(m.rpeVersion)}
                value={rpeVersion}
                options={[
                  { value: 'raise', label: t(m.rpeVersionRaise), description: t(m.rpeVersionRaiseDesc) },
                  {
                    value: 'keep',
                    label: t(m.rpeVersionKeep, { version: pkg.rpeVersion }),
                    description: t(m.rpeVersionKeepDesc),
                  },
                ]}
                onChange={setRpeVersion}
              />
            </>
          )}
          {target === 'rpe170' && ignored > 0 && (
            <>
              <p className={s.readout}>{t(m.ignoredEaseQuestion, { count: ignored })}</p>
              <ChoiceList<'drop' | 'apply'>
                legend={t(m.openSpeedSettings)}
                value={ignoredEase}
                options={[
                  { value: 'drop', label: t(m.ignoredEaseDrop), description: t(m.ignoredEaseDropDesc) },
                  { value: 'apply', label: t(m.ignoredEaseApply), description: t(m.ignoredEaseApplyDesc) },
                ]}
                onChange={setIgnoredEase}
              />
            </>
          )}
          <div className={s.actions}>
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setTarget(null);
              }}
            >
              {t(m.cancel)}
            </Button>
            <Button size="sm" variant="primary" icon="Check" onClick={confirm}>
              {t(m.switchConfirm)}
            </Button>
          </div>
        </div>
      )}

      {props.showFull && (
        <div className={s.pair}>
          <Row edit={edit} field={FIELD.pkgRpeVersion}>
            <NumberField
              label={t(m.rpeVersion)}
              value={{ kind: 'value', value: pkg.rpeVersion }}
              step={1}
              min={0}
              precision={0}
              help={{ key: m.rpeVersionHelp }}
              helpId={FIELD.pkgRpeVersion}
              badges={props.badges(FIELD.pkgRpeVersion)}
              onCommit={(v) => {
                if (v.kind !== 'number') return;
                const value = Math.max(0, Math.round(v.value));
                edit.commit(FIELD.pkgRpeVersion, { key: m.txPkg }, (tx) => {
                  Core.doc.patchPkg(tx, { patch: { rpeVersion: value } });
                });
              }}
            />
          </Row>
          <Row edit={edit} field="pkg.speedBakeTolPx">
            <NumberField
              label={t(m.speedBakeTol)}
              value={{ kind: 'value', value: pkg.speedBakeTolPx }}
              unit={t(m.px)}
              step={0.1}
              min={0.01}
              precision={3}
              help={{ key: m.speedBakeTolHelp }}
              helpId="pkg.speedBakeTolPx"
              onCommit={(v) => {
                if (v.kind !== 'number' || !(v.value > 0)) return;
                const value = v.value;
                edit.commit('pkg.speedBakeTolPx', { key: m.txPkg }, (tx) => {
                  Core.doc.patchPkg(tx, { patch: { speedBakeTolPx: value } });
                });
              }}
            />
          </Row>
        </div>
      )}
    </>
  );
}

// ---------------------------------------------------------------------------------------------------------------
// Song-select preview
// ---------------------------------------------------------------------------------------------------------------

function PreviewPanel(props: {
  readonly services: AppServices;
  readonly store: DocStore;
  readonly edit: EditKit;
  readonly badges: (field: string) => readonly BadgeProps[];
}) {
  const { services, store, edit } = props;
  const t = useT();
  const meta = useDocValue(store, metaOf, ['meta']);
  useTopicStamp(store, 'assets');
  // The decoded music (when loaded) is the reference; the asset metadata otherwise.
  const duration = musicDuration({ meta, assets: store.doc.assets }, services.audio.music);
  const issue = previewRangeIssue(meta.previewStart, meta.previewEnd, duration);
  const [playing, setPlaying] = useState(false);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  // Closing the dialog while the preview plays stops it.
  useEffect(
    () => () => {
      if (timer.current === null) return;
      clearTimeout(timer.current);
      timer.current = null;
      services.audio.transport.stop({ returnToStart: false });
    },
    [services],
  );

  const stop = (): void => {
    if (timer.current !== null) clearTimeout(timer.current);
    timer.current = null;
    services.audio.transport.stop({ returnToStart: false });
    setPlaying(false);
  };
  const play = async (): Promise<void> => {
    await services.audio.ensureContext();
    const offset = store.doc.timing.offsetMs / 1000 + store.doc.pkg.infoOffsetSec;
    const start = meta.previewStart;
    const end = meta.previewEnd ?? start + 15;
    await services.audio.transport.play({ from: Math.max(0, start - offset) });
    setPlaying(true);
    const rate = services.audio.transport.state.rate > 0 ? services.audio.transport.state.rate : 1;
    timer.current = setTimeout(stop, ((end - start) * 1000) / rate);
  };

  const setStart = (v: number): void => {
    edit.commit(FIELD.metaPreview, { key: m.txMeta }, (tx) => {
      Core.doc.patchMeta(tx, { patch: { previewStart: v } });
    });
  };
  const setEnd = (v: number | null): void => {
    edit.commit('meta.previewEnd', { key: m.txMeta }, (tx) => {
      Core.doc.patchMeta(tx, { patch: { previewEnd: v } });
    });
  };

  return (
    <>
      <Readout>{t(m.previewHelp)}</Readout>
      <div className={s.pair}>
        <Row edit={edit} field={FIELD.metaPreview}>
          <NumberField
            label={t(m.previewStart)}
            value={{ kind: 'value', value: meta.previewStart }}
            unit={t(m.seconds)}
            step={0.5}
            min={0}
            precision={3}
            helpId={FIELD.metaPreview}
            badges={props.badges(FIELD.metaPreview)}
            onCommit={(v) => {
              if (v.kind === 'number') setStart(Math.max(0, v.value));
            }}
          />
        </Row>
        <Row edit={edit} field="meta.previewEnd">
          {/* With the default end, the choice itself is the field (the End number appears for a custom end). */}
          <div data-focus-field={meta.previewEnd === null ? 'meta.previewEnd' : undefined}>
            <Segmented<'default' | 'custom'>
              ariaLabel={t(m.previewEnd)}
              size="sm"
              value={meta.previewEnd === null ? 'default' : 'custom'}
              options={[
                { value: 'default', label: t(m.previewEndDefault) },
                { value: 'custom', label: t(m.previewEndCustom) },
              ]}
              onChange={(v) => {
                setEnd(v === 'default' ? null : meta.previewStart + 15);
              }}
            />
          </div>
          {meta.previewEnd !== null && (
            <NumberField
              label={t(m.previewEnd)}
              value={{ kind: 'value', value: meta.previewEnd }}
              unit={t(m.seconds)}
              step={0.5}
              min={0}
              precision={3}
              helpId="meta.previewEnd"
              onCommit={(v) => {
                if (v.kind === 'number') setEnd(v.value);
              }}
            />
          )}
        </Row>
      </div>
      <div className={s.actions}>
        <Button
          size="sm"
          icon={playing ? 'Square' : 'Play'}
          disabledReason={meta.music === null ? { key: m.previewSongUnknown } : null}
          onClick={() => {
            if (playing) stop();
            else void play();
          }}
        >
          {t(playing ? m.previewStop : m.previewPlay)}
        </Button>
      </div>
      <Readout testId="song-length">
        {duration === null ? t(m.previewSongUnknown) : t(m.previewSongLength, { sec: fmtNum(duration, 3) })}
      </Readout>
      {issue === null ? (
        duration !== null && (
          <Callout tone="success" testId="preview-ok">
            {t(m.previewOk)}
          </Callout>
        )
      ) : (
        <Callout
          tone="danger"
          testId="preview-issue"
          actions={
            issue.kind === 'beyondSong' && duration !== null ? (
              <Button
                size="sm"
                onClick={() => {
                  edit.once({ key: m.txClampPreview }, (tx) => clampPreview(tx, { durationSec: duration }));
                }}
              >
                {t(m.previewClamp)}
              </Button>
            ) : undefined
          }
        >
          {issue.kind === 'start'
            ? t(m.previewIssueStart)
            : issue.kind === 'order'
              ? t(m.previewIssueOrder)
              : t(m.previewIssueBeyond, { limit: fmtNum(issue.limit, 3) })}
        </Callout>
      )}
    </>
  );
}
